import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { SabreLogo } from '../components/SabreLogo';
import { SparkFooter } from '../components/SparkFooter';
import { SparkInput } from '../components/SparkInput';
import { SparkMessage } from '../components/SparkMessage';
import { RequiredSetup } from '../components/mfa/RequiredSetup';
import { useAuth } from '../context/AuthContext';
import { ApiError, isMfaChallenge } from '../lib/api';

interface LoginLocationState {
  loggedOut?: boolean;
  sessionExpired?: boolean;
  idle?: boolean;
  from?: string;
}

export function LoginPage() {
  const { status, signIn, verifyMfa, applySession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as LoginLocationState;

  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [pcc, setPcc] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState('');
  const [notice, setNotice] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Set once the password was accepted for an account with two-step
  // verification: the form then asks for the authenticator code.
  const [mfaToken, setMfaToken] = useState('');
  const [code, setCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  // Set instead when the account must set up the authenticator first.
  const [setupToken, setSetupToken] = useState('');

  if (status === 'authenticated') {
    return <Navigate to={state.from ?? '/'} replace />;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError('');
    setNotice('');

    const errors: Record<string, string> = {};
    if (!userId.trim()) errors.userId = 'Username is required.';
    if (!password) errors.password = 'Password is required.';
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    try {
      const result = await signIn(userId.trim(), password, pcc.trim());
      if (isMfaChallenge(result)) {
        if (result.mfaSetupRequired) setSetupToken(result.mfaToken);
        else setMfaToken(result.mfaToken);
        setPassword('');
        return;
      }
      navigate(result.mustChangePassword ? '/change-password' : (state.from ?? '/'), { replace: true });
    } catch (error) {
      if (error instanceof ApiError) {
        setFormError(error.message);
        setFieldErrors(error.fields ?? {});
      } else {
        setFormError('Sign-in is temporarily unavailable. Please try again.');
      }
      setPassword('');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCodeSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError('');
    if (!code.trim()) {
      setFieldErrors({ code: useBackupCode ? 'Enter a backup code.' : 'Enter the 6-digit code.' });
      return;
    }
    setFieldErrors({});
    setSubmitting(true);
    try {
      const session = await verifyMfa(mfaToken, code.trim());
      navigate(session.mustChangePassword ? '/change-password' : (state.from ?? '/'), { replace: true });
    } catch (error) {
      if (error instanceof ApiError) {
        // An expired or locked challenge cannot be retried: start over.
        if (error.code === 'mfa_expired' || error.code === 'account_locked' || error.code === 'account_disabled') {
          setMfaToken('');
        }
        setFormError(error.message);
        setFieldErrors(error.fields ?? {});
      } else {
        setFormError('Sign-in is temporarily unavailable. Please try again.');
      }
      setCode('');
    } finally {
      setSubmitting(false);
    }
  };

  const startOver = () => {
    setMfaToken('');
    setCode('');
    setUseBackupCode(false);
    setFormError('');
    setFieldErrors({});
  };

  if (setupToken) {
    return (
      <div className="spark-splash-screen">
        <section className="spark-splash-screen__content">
          <div className="spark-center-block">
            <SabreLogo width={286} className="spark-logo--md" />
            <h2 className="spark-splash-screen__heading">Agency Workspace</h2>
            <RequiredSetup
              mfaToken={setupToken}
              userId={userId.trim()}
              onDone={(session) => {
                applySession(session);
                navigate(session.mustChangePassword ? '/change-password' : (state.from ?? '/'), { replace: true });
              }}
              onCancel={(message) => {
                setSetupToken('');
                setFormError(message ?? '');
              }}
            />
          </div>
        </section>
        <SparkFooter />
      </div>
    );
  }

  if (mfaToken) {
    return (
      <div className="spark-splash-screen">
        <section className="spark-splash-screen__content">
          <div className="spark-center-block">
            <SabreLogo width={286} className="spark-logo--md" />
            <h2 className="spark-splash-screen__heading">Agency Workspace</h2>

            <div className="spark-panel">
              <div className="spark-panel__content mfa-prompt">
                {formError && <SparkMessage variant="error">{formError}</SparkMessage>}

                <AuthenticatorBadge />
                <h3 className="mfa-prompt__heading">{useBackupCode ? 'Backup code' : 'Authenticator'}</h3>
                <p className="mfa-prompt__subtitle">
                  {useBackupCode
                    ? 'Enter one of the backup codes you saved. Each code works once.'
                    : 'Enter the code from your authenticator app.'}
                </p>

                <form onSubmit={handleCodeSubmit} noValidate autoComplete="off" className="mfa-prompt__form">
                  <SparkInput
                    key={useBackupCode ? 'backup' : 'totp'}
                    id="mfaCode"
                    name="code"
                    label="Enter Code"
                    placeholder={useBackupCode ? 'Example: ABCDE-FGH23' : 'Example: 123456'}
                    inputMode={useBackupCode ? 'text' : 'numeric'}
                    autoComplete="one-time-code"
                    maxLength={useBackupCode ? 16 : 6}
                    autoFocus
                    value={code}
                    error={fieldErrors.code}
                    onChange={(event) =>
                      setCode(useBackupCode ? event.target.value.toUpperCase() : event.target.value.replace(/\D/g, ''))
                    }
                    disabled={submitting}
                  />

                  <div className="spark-mar-t-1">
                    <button
                      type="submit"
                      id="submit"
                      className="spark-btn spark-btn--lg spark-btn--primary spark-btn--block"
                      disabled={submitting}
                    >
                      {submitting ? 'Verifying…' : 'Verify'}
                    </button>
                  </div>
                </form>

                <div className="login-links">
                  <button
                    type="button"
                    className="spark-btn-link"
                    onClick={() => {
                      setUseBackupCode((value) => !value);
                      setCode('');
                      setFieldErrors({});
                    }}
                  >
                    {useBackupCode ? 'Use the authenticator app' : 'Use a backup code'}
                  </button>
                  <button type="button" className="spark-btn-link" onClick={startOver}>
                    Back to sign-in
                  </button>
                </div>

                <a className="spark-link mfa-prompt__help" id="mfaAssistance" href="/mfa-assistance">
                  Report a problem with MFA
                </a>
              </div>
            </div>
          </div>
        </section>

        <SparkFooter />
      </div>
    );
  }

  const showLoggedOut = Boolean(state.loggedOut) && !formError && !notice;
  const showIdle = Boolean(state.idle) && !formError && !notice;
  const showExpired = Boolean(state.sessionExpired) && !showIdle && !formError && !notice;

  return (
    <div className="spark-splash-screen">
      <AccessibilityBadge />

      <section className="spark-splash-screen__content">
        <div className="spark-center-block">
          <SabreLogo width={286} className="spark-logo--md" />
          <h2 className="spark-splash-screen__heading">Agency Workspace</h2>

          <div className="spark-panel">
            <div className="spark-panel__content">
              {showLoggedOut && <SparkMessage variant="success">You were successfully logged out.</SparkMessage>}
              {showIdle && (
                <SparkMessage variant="info">
                  You were signed out after 15 minutes of inactivity. Sign in again with your password and
                  authenticator code.
                </SparkMessage>
              )}
              {showExpired && (
                <SparkMessage variant="info">Your session has expired. Please sign in again.</SparkMessage>
              )}
              {notice && <SparkMessage variant="info">{notice}</SparkMessage>}
              {formError && <SparkMessage variant="error">{formError}</SparkMessage>}

              <h3 className="spark-panel__heading">Enterprise sign-in</h3>

              <form onSubmit={handleSubmit} noValidate autoComplete="off">
                <SparkInput
                  id="userId"
                  name="userId"
                  label="Username"
                  placeholder="Example: 4321"
                  maxLength={20}
                  autoComplete="username"
                  autoFocus
                  value={userId}
                  error={fieldErrors.userId}
                  onChange={(event) => setUserId(event.target.value)}
                  disabled={submitting}
                />

                <SparkInput
                  id="password"
                  name="password"
                  type="password"
                  label="Password"
                  placeholder="Example: pass1234."
                  maxLength={255}
                  autoComplete="current-password"
                  value={password}
                  error={fieldErrors.password}
                  onChange={(event) => setPassword(event.target.value)}
                  disabled={submitting}
                />

                <SparkInput
                  id="group"
                  name="group"
                  label="PCC"
                  placeholder="Example: BTD0"
                  maxLength={20}
                  autoComplete="off"
                  value={pcc}
                  error={fieldErrors.pcc}
                  onChange={(event) => setPcc(event.target.value.toUpperCase())}
                  disabled={submitting}
                />

                <div className="spark-mar-t-1">
                  <button
                    type="submit"
                    id="submit"
                    className="spark-btn spark-btn--lg spark-btn--primary spark-btn--block"
                    disabled={submitting}
                  >
                    {submitting ? 'Signing in…' : 'Sign In'}
                  </button>
                </div>
              </form>

              <div className="login-links">
                <button
                  type="button"
                  className="spark-btn-link"
                  id="forgotPassword"
                  onClick={() => {
                    setFormError('');
                    setNotice(
                      'Password resets are handled by your agency administrator. Contact them to have a temporary password issued.',
                    );
                  }}
                >
                  Forgot password?
                </button>
                <a className="spark-link" id="mfaAssistance" href="/mfa-assistance">
                  Report a problem with MFA
                </a>
              </div>
            </div>
          </div>

          <div className="sso-content">
            <div className="kopis-sso-actions">
              <div className="sso-link">
                <b>
                  <a className="sign-in-links" id="ssoLogin" href="/sso/login">
                    Sign in with SSO
                  </a>
                </b>
              </div>
              <div className="separator-dot">·</div>
              <div className="sabreid-link">
                <b>
                  <a className="sign-in-links" id="sabreIdLogin" href="/sabreid/login">
                    Sign in with SabreID
                  </a>
                </b>
              </div>
              <div className="separator-dot">·</div>
              <div className="other-methods-link">
                <b>
                  <a className="sign-in-links" id="otherSignInType" href="/welcome">
                    Other sign-in method
                  </a>
                </b>
              </div>

              <div className="sso-message">
                <p>
                  <span>To learn more about SSO sign-in methods visit our </span>
                  <a className="sign-in-links" id="ssoDetails" href="/sso-details" style={{ color: '#202224' }}>
                    SSO page
                  </a>
                  <br />
                  <span>on Sabre Store or contact your system administrator.</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SparkFooter />
    </div>
  );
}

/** The device-and-shield mark above the code field, echoing the host's MFA screen. */
function AuthenticatorBadge() {
  return (
    <div className="mfa-prompt__badge" aria-hidden="true">
      <svg viewBox="0 0 48 48" width="44" height="44" fill="none" stroke="currentColor" strokeWidth="2.2">
        {/* A phone... */}
        <rect x="13" y="6" width="22" height="36" rx="3" />
        <line x1="21" y1="37.5" x2="27" y2="37.5" strokeLinecap="round" />
        {/* ...with a shield and tick on its face. */}
        <path
          d="M24 14l6 2.2v4.3c0 3.8-2.5 6.6-6 7.7-3.5-1.1-6-3.9-6-7.7v-4.3z"
          fill="currentColor"
          stroke="none"
        />
        <path d="M21.7 20.7l1.7 1.7 3-3.2" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

/** The floating accessibility control that sits in the corner of the portal. */
function AccessibilityBadge() {
  return (
    <button type="button" className="spark-a11y-badge" aria-label="Accessibility options">
      {/* Traced from the host: a solid diamond with a square window, and a
          small diamond centred in the window. */}
      <svg viewBox="0 0 40 40" width="40" height="40" fill="currentColor" aria-hidden="true">
        <path fillRule="evenodd" d="M20 0 40 20 20 40 0 20ZM10 10V30H30V10Z" />
        <path d="M20 14 26 20 20 26 14 20Z" />
      </svg>
    </button>
  );
}
